/**
 * Data half of the add-tile dialog (S6): source tabs (SQL + Templates + Ask
 * AI live; Visual is the last disabled placeholder S8 replaces in the SAME
 * release — the S19 gate asserts none remain), the SQL pane, preview results
 * (trust strip, result-set picker, row preview). Tile-type suggestions live
 * on the config pane's type cards — the dialog computes them once.
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SqlMonacoHandle } from '../../../common/utils/sqlMonacoTypes';
import type { ShapeSuggestions } from '../../utils/shapeSuggest';
import { REFRESH_INTERVAL_OPTIONS } from './addTileFormModel';
import { SqlSourcePane } from './SqlSourcePane';
import { TrustStrip } from './TrustStrip';
import type { TilePreview } from './useTilePreview';

const intervalLabel = (seconds: number): string =>
  seconds < 60 ? `Every ${seconds}s` : seconds < 3600 ? `Every ${seconds / 60} min` : 'Every hour';

export type AddTileSource = 'visual' | 'templates' | 'sql' | 'ai';

/** All four sources live (S7/S8/S9) — no placeholder tabs remain (S19 gate). */
const SOURCE_TABS: ReadonlyArray<{
  key: AddTileSource;
  label: string;
  disabled: boolean;
  hint: string | undefined;
}> = [
  { key: 'visual', label: 'Visual', disabled: false, hint: undefined },
  { key: 'templates', label: 'Templates', disabled: false, hint: undefined },
  { key: 'sql', label: 'SQL', disabled: false, hint: undefined },
  { key: 'ai', label: 'Ask AI', disabled: false, hint: undefined },
];

/** The former suggestion row's space goes to the preview table (more rows). */
const PREVIEW_ROWS_SHOWN = 12;

export function AddTileDataPane({
  activeSource,
  onSourceChange,
  sourcePanes,
  connectionId,
  onConnectionChange,
  database,
  onDatabaseChange,
  sql,
  onSqlChange,
  editorRef,
  preview,
  onPreview,
  resultSetIndex,
  onResultSetIndexChange,
  refresh,
  onRefreshChange,
  suggestions,
}: {
  activeSource: AddTileSource;
  onSourceChange: (source: AddTileSource) => void;
  /** Non-SQL source panes (S7/S8/S9 mount theirs here); 'sql' is built in. */
  sourcePanes?: Partial<Record<Exclude<AddTileSource, 'sql'>, React.ReactNode>>;
  connectionId: string;
  onConnectionChange: (id: string) => void;
  /** Form-owned database (shared chrome — see SqlSourcePane). */
  database?: string;
  onDatabaseChange: (database: string) => void;
  sql: string;
  onSqlChange: (sql: string) => void;
  /** S8: dialog-owned live handle onto the always-mounted SQL editor. */
  editorRef?: React.Ref<SqlMonacoHandle>;
  preview: TilePreview;
  onPreview: () => void;
  resultSetIndex: number;
  onResultSetIndexChange: (index: number) => void;
  refresh: { mode: 'manual' | 'interval'; seconds: number };
  onRefreshChange: (refresh: { mode: 'manual' | 'interval'; seconds: number }) => void;
  /** Dialog-computed shape suggestions — only the healthy-empty note reads them here. */
  suggestions: ShapeSuggestions | null;
}) {
  const result = preview.state.status === 'done' ? preview.state.result : null;
  const picked = result?.resultSets[resultSetIndex] ?? result?.resultSets[0];

  return (
    <div className="flex min-h-0 flex-col gap-2.5">
      <div className="flex items-center gap-1 border-b border-border" role="tablist">
        {SOURCE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={tab.key === activeSource}
            disabled={tab.disabled}
            title={tab.hint}
            onClick={() => onSourceChange(tab.key)}
            className={`-mb-px border-b-2 px-2.5 py-1.5 text-xs font-medium ${
              tab.key === activeSource
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground disabled:opacity-50'
            }`}
            data-testid={`add-tile-source-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Always mounted (S8): the connection + database + Preview row is shared
          chrome for every source; the editor inside hides off the SQL tab but
          keeps its handle alive for the Visual tab's commits + template/AI
          insertion. */}
      <SqlSourcePane
        connectionId={connectionId}
        onConnectionChange={onConnectionChange}
        database={database}
        onDatabaseChange={onDatabaseChange}
        sql={sql}
        onSqlChange={onSqlChange}
        onPreview={onPreview}
        previewRunning={preview.state.status === 'running'}
        editorRef={editorRef}
        editorHidden={activeSource !== 'sql'}
      />
      {activeSource !== 'sql' && (sourcePanes?.[activeSource] ?? null)}

      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Refresh</span>
        <Select
          value={refresh.mode === 'manual' ? 'manual' : String(refresh.seconds)}
          onValueChange={(v) =>
            onRefreshChange(
              v === 'manual'
                ? { mode: 'manual', seconds: refresh.seconds }
                : { mode: 'interval', seconds: Number(v) },
            )
          }
        >
          <SelectTrigger className="h-7 w-40 text-xs" data-testid="add-tile-refresh-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manual" data-testid="refresh-option-manual">
              Manual
            </SelectItem>
            {REFRESH_INTERVAL_OPTIONS.map((seconds) => (
              <SelectItem
                key={seconds}
                value={String(seconds)}
                data-testid={`refresh-option-${seconds}`}
              >
                {intervalLabel(seconds)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <TrustStrip
        preview={preview.state}
        durationsMs={preview.durationsMs}
        refresh={refresh}
        resultSetIndex={resultSetIndex}
      />

      {/* Healthy-empty (0 rows, real columns) sits with the preview status —
          it's a data statement, not a tile-type concern. */}
      {suggestions?.emptyWithValidShape && (
        <div className="text-xs text-muted-foreground" data-testid="add-tile-empty-shape-note">
          No rows right now — that can be healthy for monitoring queries. The tile will show an
          empty state and fill in when data appears.
        </div>
      )}

      {result && result.resultSets.length > 1 && (
        <div
          className="flex items-center gap-1 text-xs"
          data-testid="add-tile-resultset-picker"
        >
          <span className="text-muted-foreground">Result set:</span>
          {result.resultSets.map((set) => (
            <button
              key={set.index}
              type="button"
              onClick={() => onResultSetIndexChange(set.index)}
              className={`rounded-sm border px-2 py-0.5 ${
                set.index === resultSetIndex
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border text-muted-foreground hover:bg-accent'
              }`}
              data-testid={`resultset-option-${set.index}`}
            >
              {set.index + 1} · {set.rowCount} {set.rowCount === 1 ? 'row' : 'rows'}
            </button>
          ))}
        </div>
      )}

      {picked && picked.previewRows.length > 0 && (
        <div
          className="min-h-0 flex-1 overflow-auto rounded-md border border-border"
          data-testid="add-tile-preview-table"
        >
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted/60">
              <tr>
                {picked.columns.map((col) => (
                  <th key={col.name} className="px-2 py-1 text-left font-medium">
                    {col.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {picked.previewRows.slice(0, PREVIEW_ROWS_SHOWN).map((row, i) => (
                <tr key={i} className="border-t border-border/60">
                  {picked.columns.map((col) => {
                    const value = (row as Record<string, unknown>)[col.id ?? col.name];
                    return (
                      <td key={col.name} className="max-w-48 truncate px-2 py-1">
                        {value === null || value === undefined ? (
                          <span className="italic text-muted-foreground">∅</span>
                        ) : (
                          String(value)
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
